import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './SimplisticTestimonial.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// ✅ GLASSMORPHISM HELPER: Konvertiert Farbe zu transparenter Version
function getGlassmorphismColor(color) {
    // Entferne # wenn vorhanden
    const hex = color.replace('#', '');
    
    // Konvertiere zu RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Verwende 20% Opacity für echten Glassmorphism-Effekt
    return `rgba(${r}, ${g}, ${b}, 0.2)`;
}

function SimplisticTestimonial({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // ✅ INLINE-EDITING SYSTEM - EXAKT WIE HEROHEADER
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // ✅ RESPONSIVE LAYOUT DETECTION - NEW LAYOUT SYSTEM 2025
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // ✅ MOBILE BREAKPOINT DETECTION (750px Standard) - NEW LAYOUT SYSTEM 2025
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750); // ✅ Standardized Breakpoint
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // ✅ INLINE-EDITING FUNCTIONS - EXAKT WIE HEROHEADER
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // ✅ RESPONSIVE WIDGET STYLES mit generateLayoutStyles - NEW LAYOUT SYSTEM 2025
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        // ✅ BACKGROUND IMAGE LOGIC: Nur backgroundColor wenn kein Background Image aktiv
        backgroundColor: (data.showBackgroundImage && data.backgroundImage) ? 'transparent' : data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Property
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile: Template-Default
                (data.height || 650)         // Desktop: Template-Default
            )
        }),
        // ✅ BACKGROUND IMAGE: Mindesthöhe für sichtbares Background
        ...(data.showBackgroundImage && data.backgroundImage && {
            minHeight: appendPx(isMobileLayout ? 
                Math.max(data.heightMobile || 1200, 400) : // Mobile: Mindestens 400px
                Math.max(data.height || 650, 500)          // Desktop: Mindestens 500px
            )
        })
    };

    // ✅ RENDER EDITABLE TEXT FUNCTION - NEW LAYOUT SYSTEM 2025 Standards
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            const layoutData = originalLayout;
            const isTextarea = Element !== 'button';
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // GRADIENT-FIX: Grauer Text während Bearbeitung
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            // Original-Text-Eigenschaften
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // BUTTONS: Input-Felder mit exakter Größen-Erhaltung
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => setInlineValue(e.target.value)}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        minWidth: layoutData.width || style.minWidth,
                        maxWidth: layoutData.width || 'none',
                        minHeight: layoutData.height || 'auto',
                        maxHeight: layoutData.height || 'none',
                        marginTop: layoutData.marginTop || '0',
                        marginBottom: layoutData.marginBottom || '0',
                        marginLeft: layoutData.marginLeft || '0',
                        marginRight: layoutData.marginRight || '0',
                        backgroundColor: style.backgroundColor,
                        border: style.border || 'none',
                        borderRadius: style.borderRadius,
                        padding: style.padding,
                        display: style.display,
                        alignItems: style.alignItems,
                        justifyContent: style.justifyContent,
                        alignSelf: style.alignSelf,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                />
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: Element === 'button' ? 'pointer' : 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: Element === 'button' ? style.padding : '4px 8px',
                    borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '4px'),
                    border: Element === 'button' ? style.border : 'none',
                    boxShadow: Element === 'button' ? style.boxShadow : 'none'
                }}
                onClick={handleElementClick}
                onMouseEnter={(e) => {
                    if (Element !== 'button') {
                        e.target.style.cursor = 'text';
                    } else {
                        e.target.style.cursor = 'pointer';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.cursor = Element === 'button' ? 'pointer' : 'text';
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // Smart Outside-Click Handler - nur für echte Außenklicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            // Smarte Erkennung: Ist es ein echter Außenklick?
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            // Definiere was ein "echter Außenklick" ist
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            console.log('🔍 SimplisticTestimonial smart outside click analysis:', {
                isInlineEditing,
                target: event.target.tagName,
                targetClass: event.target.className,
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isPagePreview,
                isRealOutsideClick,
                inlineValue
            });
            
            if (isRealOutsideClick) {
                console.log('✅ Real outside click confirmed - saving and closing');
                // Speichern und beenden
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                    console.log('💾 Saved data:', updatedData);
                }
                // Edit-Modus beenden
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            console.log('🎯 Adding smart outside click listener for SimplisticTestimonial field:', isInlineEditing);
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                console.log('🧹 Removing smart outside click listener');
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);


    // Testimonials aus data extrahieren - dynamisches Array
    const testimonials = data.testimonials || [];

    // Layout Helper Functions - Grid oder Full Width
    const getLayoutStyle = () => {
        // Check if it's a full-width layout
        const isFullWidthLayout = [
            'full-width-classic',
            'full-width-minimal',
            'full-width-bordered',
            'full-width-3d-stack',
            'full-width-3d-layered',
            'full-width-fancy'
        ].includes(data.testimonialLayout);

        if (isFullWidthLayout) {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: appendPx(getResponsiveValue(data, 'testimonialSpacing', isMobileLayout, 20)),
                width: '100%',
                maxWidth: '1000px',
                margin: '0 auto'
            };
        }

        // Default grid layout for existing layouts - 🚀 Revolutionary Layout System
        return {
            display: 'grid',
            // ✅ SMART GRID: Immer 2 Spalten auf Desktop, 1 Spalte auf Mobile
            gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(2, 1fr)',
            // ✅ NEW: Separate horizontale und vertikale Gaps
            columnGap: appendPx(getResponsiveValue(data, 'testimonialHorizontalGap', isMobileLayout, 30)),
            rowGap: appendPx(getResponsiveValue(data, 'testimonialSpacing', isMobileLayout, 30)),
            alignItems: 'stretch', // Gleiche Höhe für alle Cards
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto'
        };
    };

    // Get testimonial card style based on layout
    const getTestimonialCardStyle = (index) => {
        const isFullWidthLayout = [
            'full-width-classic',
            'full-width-minimal',
            'full-width-bordered',
            'full-width-3d-stack',
            'full-width-3d-layered',
            'full-width-fancy'
        ].includes(data.testimonialLayout);

        const baseStyle = {
            // ✅ GLASSMORPHISM: Deutlich transparentere Background Color
            backgroundColor: data.testimonialGlassmorphism ? 
                getGlassmorphismColor(data.testimonialBackgroundColor || '#ffffff') :
                (data.testimonialBackgroundColor || '#ffffff'),
            border: data.testimonialBorder ? `1px solid ${data.testimonialBorderColor || '#e0e0e0'}` : 'none',
            borderRadius: data.testimonialBorderRadius !== undefined ? appendPx(data.testimonialBorderRadius) : appendPx(12),
            // ✅ ERWEITERTE PADDING-OPTIONEN: Nur Horizontal/Vertical
            paddingLeft: appendPx(getResponsiveValue(data, 'testimonialPaddingHorizontal', isMobileLayout, 24)),
            paddingRight: appendPx(getResponsiveValue(data, 'testimonialPaddingHorizontal', isMobileLayout, 24)),
            paddingTop: appendPx(getResponsiveValue(data, 'testimonialPaddingVertical', isMobileLayout, 24)),
            paddingBottom: appendPx(getResponsiveValue(data, 'testimonialPaddingVertical', isMobileLayout, 24)),
            boxShadow: data.testimonialShadow ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            // ✅ GLASSMORPHISM: Backdrop Filter nur wenn Background Image vorhanden
            ...(data.testimonialGlassmorphism && data.showBackgroundImage && data.backgroundImage && {
                backdropFilter: `blur(${data.testimonialGlassStrength || 10}px)`,
                WebkitBackdropFilter: `blur(${data.testimonialGlassStrength || 10}px)`, // Safari Support
                border: `1px solid rgba(255, 255, 255, 0.2)` // Subtle border für Glass-Effect
            }),
            // ✅ SMART WIDTH: 100% nur wenn keine Max Width gesetzt ist
            width: data.testimonialCardMaxWidth ? 'auto' : '100%',
            // ✅ RESPONSIVE: Card Height
            ...(data.testimonialCardHeight && {
                minHeight: appendPx(getResponsiveValue(data, 'testimonialCardHeight', isMobileLayout, 300))
            }),
            // ✅ NEW: Card Max Width - IMMER anwenden
            maxWidth: appendPx(getResponsiveValue(data, 'testimonialCardMaxWidth', isMobileLayout, 600))
        };

        // Full-width layouts - horizontal layout
        if (isFullWidthLayout) {
            const isFullHeightImage = data.testimonialImageMode === true;
            
            const fullWidthBaseStyle = {
                ...baseStyle,
                // ✅ RESPONSIVE DIRECTION: Mobile = Column (Bild oben, Text unten), Desktop = Row
                flexDirection: isMobileLayout ? 'column' : 'row',
                textAlign: isMobileLayout ? 'center' : 'left',
                maxWidth: '100%',
                // ✅ MOBILE LAYOUT: Optimierter Abstand zwischen Bild und Text
                gap: isMobileLayout ? '16px' : '20px',
                // ✅ FULL-HEIGHT IMAGE: Mindesthöhe für Full-Height Images nur auf Desktop
                ...(isFullHeightImage && !isMobileLayout && {
                    minHeight: '200px'
                }),
                // ✅ CARD HEIGHT: Überschreibt baseStyle minHeight für Full-Width Layouts
                ...(data.testimonialCardHeight && data.testimonialCardHeight !== 'auto' && {
                    minHeight: appendPx(isMobileLayout ? 
                        (data.testimonialCardHeightMobile || data.testimonialCardHeight || 300) : 
                        (data.testimonialCardHeight || 300)
                    )
                })
            };

            switch (data.testimonialLayout) {
                case 'full-width-classic':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                        }),
                        // ✅ FIX: RESPEKTIERT USER BORDER TOGGLE - Nur Border wenn User es aktiviert hat
                        ...(data.testimonialBorder && {
                            border: `1px solid ${data.testimonialBorderColor || '#e9ecef'}`
                        })
                    };
                case 'full-width-minimal':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ Minimal Layout: Immer linker Border-Akzent
                        borderLeft: '4px solid #007bff'
                    };
                case 'full-width-bordered':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(145deg, #f7f8fc 0%, #ffffff 100%)'
                        }),
                        // ✅ FIX: RESPEKTIERT USER BORDER TOGGLE - Nur Border wenn User es aktiviert hat
                        ...(data.testimonialBorder && {
                            border: `2px solid ${data.testimonialBorderColor || '#e3e8ee'}`
                        }),
                        // ✅ RESPEKTIERT USER SHADOW: Kombiniert mit Layout-spezifischem Shadow
                        ...(data.testimonialShadow && {
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 2px 4px rgba(0, 0, 0, 0.08)'
                        }),
                        position: 'relative'
                    };
                case 'full-width-3d-stack':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                        }),
                        position: 'relative',
                        transformStyle: 'preserve-3d'
                    };
                case 'full-width-3d-layered':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && {
                            background: 'linear-gradient(135deg, #f1f3f4 0%, #ffffff 100%)'
                        }),
                        position: 'relative',
                        transformStyle: 'preserve-3d'
                    };
                case 'full-width-fancy':
                    return {
                        ...fullWidthBaseStyle,
                        // ✅ FANCY LAYOUT: Minimalistic, clean design
                        backgroundColor: data.testimonialBackgroundColor || 'transparent',
                        border: 'none', // Force no border
                        borderRadius: '0px', // Force no border radius
                        boxShadow: 'none', // Force no shadow
                        padding: '0px', // Force no padding
                        position: 'relative',
                        overflow: 'hidden'
                    };
                default:
                    return fullWidthBaseStyle;
            }
        }

        // Grid layouts - vertical layout (existing logic)
        const gridBaseStyle = {
            // ✅ GLASSMORPHISM: Deutlich transparentere Background Color
            backgroundColor: data.testimonialGlassmorphism ? 
                getGlassmorphismColor(data.testimonialBackgroundColor || '#ffffff') :
                (data.testimonialBackgroundColor || '#ffffff'),
            border: data.testimonialBorder ? `1px solid ${data.testimonialBorderColor || '#e0e0e0'}` : 'none',
            borderRadius: data.testimonialBorderRadius !== undefined ? appendPx(data.testimonialBorderRadius) : appendPx(12),
            // ✅ ERWEITERTE PADDING-OPTIONEN: Nur Horizontal/Vertical
            paddingLeft: appendPx(getResponsiveValue(data, 'testimonialPaddingHorizontal', isMobileLayout, 24)),
            paddingRight: appendPx(getResponsiveValue(data, 'testimonialPaddingHorizontal', isMobileLayout, 24)),
            paddingTop: appendPx(getResponsiveValue(data, 'testimonialPaddingVertical', isMobileLayout, 24)),
            paddingBottom: appendPx(getResponsiveValue(data, 'testimonialPaddingVertical', isMobileLayout, 24)),
            boxShadow: data.testimonialShadow ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '16px',
            // ✅ GLASSMORPHISM: Backdrop Filter nur wenn Background Image vorhanden
            ...(data.testimonialGlassmorphism && data.showBackgroundImage && data.backgroundImage && {
                backdropFilter: `blur(${data.testimonialGlassStrength || 10}px)`,
                WebkitBackdropFilter: `blur(${data.testimonialGlassStrength || 10}px)`, // Safari Support
                border: `1px solid rgba(255, 255, 255, 0.2)` // Subtle border für Glass-Effect
            }),
            // ✅ FIX: Grid Layout Cards sollen die verfügbare Grid-Spalten-Breite nutzen
            width: '100%',
            // ✅ RESPONSIVE: Card Height
            ...(data.testimonialCardHeight && {
                minHeight: appendPx(isMobileLayout ? 
                    (data.testimonialCardHeightMobile || data.testimonialCardHeight || 300) : 
                    (data.testimonialCardHeight || 300)
                )
            }),
            // ✅ FIX: Entferne maxWidth für bessere Grid-Zentrierung - Cards nutzen verfügbare Grid-Breite
            // maxWidth wird nur angewendet wenn User explizit eine Breite gesetzt hat
            ...(data.testimonialCardMaxWidth && {
                maxWidth: appendPx(getResponsiveValue(data, 'testimonialCardMaxWidth', isMobileLayout, 600)),
                // ✅ CENTERING: Wenn maxWidth gesetzt ist, zentriere die Card in der Grid-Zelle
                margin: '0 auto'
            })
        };

        switch (data.testimonialLayout) {
            case 'masonry-flow':
                return {
                    ...gridBaseStyle,
                    // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults, wenn User-Property nicht gesetzt (aber nicht bei Glassmorphismus)
                    ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)'
                    })
                    // ✅ REMOVED: height: '100%' - damit User Card Height funktioniert
                };
                case 'luxury-cards':
                    return {
                        ...gridBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(135deg, #fff9e6 0%, #ffffff 50%, #fef7f0 100%)'
                        }),
                        // ✅ FIX: RESPEKTIERT USER BORDER TOGGLE - Nur Border wenn User es aktiviert hat
                        ...(data.testimonialBorder && {
                            border: `2px solid ${data.testimonialBorderColor || 'rgba(212, 165, 116, 0.2)'}`
                        }),
                        position: 'relative',
                        overflow: 'hidden'
                        // ✅ REMOVED: height: '100%' - damit User Card Height funktioniert
                    };
                case 'paper-cards':
                    return {
                        ...gridBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(145deg, #fefdfb 0%, #f9f7f4 100%)'
                        }),
                        // ✅ FIX: RESPEKTIERT USER BORDER TOGGLE - Nur Border wenn User es aktiviert hat
                        ...(data.testimonialBorder && {
                            border: `1px solid ${data.testimonialBorderColor || 'rgba(139, 115, 85, 0.2)'}`
                        }),
                        // ✅ RESPEKTIERT USER SHADOW: Kombiniert mit Layout-spezifischem Shadow
                        ...(data.testimonialShadow && {
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 4px 12px rgba(139, 115, 85, 0.15)'
                        }),
                        position: 'relative',
                        clipPath: 'polygon(0 0, 97% 0, 100% 3%, 100% 100%, 3% 100%, 0 97%)'
                        // ✅ REMOVED: height: '100%' - damit User Card Height funktioniert
                    };
            default: // classic-grid
                return {
                    ...gridBaseStyle,
                        // ✅ RESPEKTIERT USER-PROPERTIES: Nur Layout-spezifische Defaults (aber nicht bei Glassmorphismus)
                        ...((!data.testimonialBackgroundColor || data.testimonialBackgroundColor === '#ffffff') && !data.testimonialGlassmorphism && {
                            background: 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)'
                        })
                    // ✅ REMOVED: height: '100%' - damit User Card Height funktioniert
                };
        }
    };

    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    @keyframes simplicisticTestimonialUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                `}
            </style>
            
            <div 
                className={`simplistic-testimonial-widget layout-${data.layoutType || 'classic-grid'}`}
                style={widgetStyle}
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
                {/* ✅ BACKGROUND IMAGE SYSTEM - Vollflächig über das gesamte Widget */}
                {data.showBackgroundImage && data.backgroundImage && (() => {
                    // ✅ GLASSMORPHISM DEBUG
                    console.log('🪟 Glassmorphism Debug:', {
                        testimonialGlassmorphism: data.testimonialGlassmorphism,
                        showBackgroundImage: data.showBackgroundImage,
                        backgroundImage: !!data.backgroundImage,
                        testimonialGlassStrength: data.testimonialGlassStrength
                    });
                    
                    const blurValue = data.backgroundImageBlur || 0;
                    const hasBlur = blurValue > 0;
                    // ✅ BLUR FIX: Erweitere das Background Image um den Blur-Wert um weiße Ränder zu vermeiden
                    const blurOffset = hasBlur ? blurValue * 2 : 0;
                    
                    return (
                        <div style={{
                            position: 'absolute',
                            // ✅ BLUR FIX: Erweitere den Container um den Blur-Offset
                            top: hasBlur ? `-${blurOffset}px` : 0,
                            left: hasBlur ? `-${blurOffset}px` : 0,
                            right: hasBlur ? `-${blurOffset}px` : 0,
                            bottom: hasBlur ? `-${blurOffset}px` : 0,
                            backgroundImage: `url(${data.backgroundImage})`,
                            backgroundSize: data.backgroundImageSize || 'cover',
                            backgroundPosition: data.backgroundImagePosition || 'center',
                            backgroundRepeat: 'no-repeat',
                            opacity: (data.backgroundImageOpacity || 50) / 100,
                            filter: hasBlur ? `blur(${blurValue}px)` : 'none',
                            borderRadius: 'inherit',
                            zIndex: 0,
                            pointerEvents: 'none'
                        }} />
                    );
                })()}
            {/* Delete Button */}
            <div>
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'SimplisticTestimonial'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [SIMPLICISTICTESTIMONIAL] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}
                
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}
            </div>

            {/* Content Container - Einfach und sauber */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: appendPx(getResponsiveValue(data, 'contentSpacing', isMobileLayout, 30)),
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: appendPx(data.maxWidth || 1000),
                margin: '0 auto',
                position: 'relative', // ✅ Für z-index layering
                zIndex: 2 // ✅ FIXED: Sicher über dem Background Image (zIndex: 0)
            }}>

                {/* Title - Inline Editable mit HighlightText Support */}
                {data.showTitleText !== false && (
                    <div>
                        {/* ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv */}
                        {(!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                            // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                            renderEditableText(
                                'titleText',
                                data.titleText,
                                'h2',
                                {
                                    // Title Kombinierte Effects
                                    ...((!data.titleEffect || data.titleEffect === 'none') && {
                                        color: data.titleColor || '#000000'
                                    }),
                                    ...getTextEffectStyles(data.titleEffect),
                                    
                                    fontSize: appendPx(data.titleSize || 32),
                                    fontWeight: data.titleWeight || '600',
                                    fontFamily: data.titleFont || fonts.globalBigFont,
                                    margin: '0',
                                    lineHeight: '1.3',
                                    // Responsive Font-Sizes
                                    '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
                                    '--normal-size': appendPx(data.titleSize || 32),
                                    '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
                                },
                                'What Our Customers Say'
                            )
                        ) : (
                            // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                            <h2 style={{
                                // Title Kombinierte Effects
                                ...((!data.titleEffect || data.titleEffect === 'none') && {
                                    color: data.titleColor || '#000000'
                                }),
                                ...getTextEffectStyles(data.titleEffect),
                                
                                fontSize: appendPx(data.titleSize || 32),
                                fontWeight: data.titleWeight || '600',
                                fontFamily: data.titleFont || fonts.globalBigFont,
                                margin: '0',
                                lineHeight: '1.3',
                                textAlign: 'center',
                                whiteSpace: 'pre-wrap',
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
                                '--normal-size': appendPx(data.titleSize || 32),
                                '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
                            }}>
                                <HighlightText
                                    text={data.titleText}
                                    highlightWords={data.titleHighlightWords}
                                    baseStyle={{}}
                                    highlightFont={data.titleHighlightFont || fonts.globalSmallFont}
                                    highlightColor={data.titleHighlightColor}
                                    highlightWeight={data.titleHighlightWeight}
                                    highlightSize={data.titleHighlightSize || data.titleSize}
                                    highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile}
                                    highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig}
                                    highlightEffect={data.titleHighlightEffect}
                                    highlightMarginLeft={data.titleHighlightMarginLeft}
                                    highlightMarginRight={data.titleHighlightMarginRight}
                                    getTextEffectStyles={getTextEffectStyles}
                                />
                            </h2>
                        )}
                    </div>
                )}

                {/* Subtitle - Inline Editable - Mit Show/Hide Toggle */}
                {data.showSubtitleText !== false && renderEditableText(
                    'subtitleText',
                    data.subtitleText,
                    'p',
                    {
                        // Subtitle Kombinierte Effects
                        ...((!data.subtitleEffect || data.subtitleEffect === 'none') && {
                            color: data.subtitleColor || '#666666'
                        }),
                        ...getTextEffectStyles(data.subtitleEffect),
                        
                        fontSize: appendPx(data.subtitleSize || 18),
                        fontWeight: data.subtitleWeight || '400',
                        fontFamily: data.subtitleFont || fonts.globalBigFont || (fonts.globalSmallFont !== 'Arial' ? fonts.globalSmallFont : fonts.globalBigFont) || 'Helvetica, sans-serif',
                        margin: '0',
                        lineHeight: '1.6',
                        maxWidth: appendPx(data.subtitleMaxWidth || 700),
                        // Responsive Font-Sizes
                        '--mobile-size': appendPx(data.subtitleSizeMobile || data.subtitleSize || 18),
                        '--normal-size': appendPx(data.subtitleSize || 18),
                        '--big-size': appendPx(data.subtitleSizeBig || data.subtitleSize || 18)
                    },
                    'Discover what our amazing customers have to say about their experience...'
                )}

                {/* Testimonials - Layout-abhängig */}
                {testimonials.length > 0 && (
                    <div style={{
                        ...getLayoutStyle(),
                        width: '100%',
                        // ✅ NEW: Zusätzlicher Top-Spacing für Testimonials
                        marginTop: appendPx(getResponsiveValue(data, 'testimonialsTopSpacing', isMobileLayout, 0))
                    }}>
                        {testimonials.map((testimonial, index) => {
                            const isFullWidthLayout = [
                                'full-width-classic',
                                'full-width-modern',
                                'full-width-elegant',
                                'full-width-minimal',
                                'full-width-bordered',
                                'full-width-3d-stack',
                                'full-width-3d-deep',
                                'full-width-3d-layered',
                                'full-width-fancy'
                            ].includes(data.testimonialLayout);

                            const cardStyle = getTestimonialCardStyle(index);
                            // ✅ VERWENDE CONTAINER-BASIERTE MOBILE-ERKENNUNG
                            const isMobile = isMobileLayout;

                            return (
                                <div
                                    key={index}
                                    style={{
                                        ...cardStyle,
                                        '--index': index
                                    }}
                                    className={`testimonial-card ${data.testimonialLayout || 'classic-grid'}`}
                                >
                                    {/* Full-Width Layout Structure */}
                                    {isFullWidthLayout ? (
                                        <>
                                            {/* ✅ NEW: Dynamic Image Section - Round oder Full-Height */}
                                            {testimonial.image && data.testimonialImageMode ? (
                                                // ✅ FULL-HEIGHT IMAGE MODE
                                                <div style={{
                                                    width: isMobile ? 
                                                        `${data.fullHeightImageWidthMobile !== undefined ? data.fullHeightImageWidthMobile : 70}%` : // ✅ Mobile: User-einstellbare Prozent-Breite
                                                        appendPx(data.fullHeightImageWidth || 120), // Desktop: Pixel-Breite
                                                    height: isMobile ? 
                                                        'auto' : // ✅ Mobile: Auto Height für responsive Images
                                                        appendPx(data.fullHeightImageHeight || 250), // Desktop: User-definierte Höhe
                                                    minHeight: isMobile ? 'auto' : appendPx(data.fullHeightImageHeight || 250),
                                                    overflow: 'hidden',
                                                    // ✅ FIX: Border Radius respektiert User-Einstellung auch auf Mobile
                                                    borderRadius: data.testimonialBorderRadius !== undefined ? appendPx(data.testimonialBorderRadius) : appendPx(12),
                                                    flexShrink: 0,
                                                    order: isMobile ? 0 : (data.fullHeightImagePosition === 'right' ? 2 : 0), // ✅ Mobile: Image immer oben (order: 0)
                                                    // ✅ MOBILE: Zentriere Bild horizontal
                                                    alignSelf: isMobile ? 'center' : 'stretch',
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    // ✅ MOBILE: Optimierte Image-Darstellung
                                                    ...(isMobile && {
                                                        aspectRatio: '4/3', // Bessere Aspect Ratio für Mobile
                                                        // ✅ FIX: Entferne doppelte maxWidth - verwende nur width
                                                        marginBottom: '8px' // Zusätzlicher Abstand zum Text
                                                    })
                                                }}>
                                                    <img
                                                        src={testimonial.image}
                                                        alt={testimonial.author}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover', // ✅ Füllt Container komplett aus
                                                            borderRadius: 'inherit' // ✅ Übernimmt Container border-radius
                                                        }}
                                                    />
                                                </div>
                                            ) : testimonial.image ? (
                                                // ✅ ROUND IMAGE MODE (Standard)
                                                <div style={{
                                                    width: appendPx(data.testimonialImageSize || 80),
                                                    height: appendPx(data.testimonialImageSize || 80),
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    border: data.testimonialImageBorder !== false ? 
                                                        `${data.testimonialImageBorderWidth || 2}px solid ${data.testimonialImageBorderColor || '#e0e0e0'}` : 
                                                        'none',
                                                    flexShrink: 0,
                                                    alignSelf: 'center', // ✅ IMMER vertikal zentriert
                                                    // ✅ MOBILE: Optimierte Round Image Darstellung
                                                    ...(isMobile && {
                                                        marginBottom: '8px' // Zusätzlicher Abstand zum Text
                                                    })
                                                }}>
                                                    <img
                                                        src={testimonial.image}
                                                        alt={testimonial.author}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            ) : null}

                                            {/* Content Section */}
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px',
                                                flex: 1,
                                                minWidth: 0, // Allows text to wrap properly
                                                order: isMobile ? 1 : 1, // ✅ Mobile: Content nach Image (1), Desktop: Content in der Mitte (1)
                                                // ✅ FULL-HEIGHT IMAGE: Zusätzliche Padding nur auf Desktop
                                                ...(data.testimonialImageMode === true && testimonial.image && !isMobile && {
                                                    paddingLeft: data.fullHeightImagePosition === 'left' ? '20px' : '0',
                                                    paddingRight: data.fullHeightImagePosition === 'right' ? '20px' : '0'
                                                }),
                                                // ✅ TEXT ALIGNMENT: Abhängig von Full Height Image Position
                                                textAlign: !isMobile && data.testimonialImageMode === true && testimonial.image ? 
                                                    (data.fullHeightImagePosition === 'right' ? 'right' : 'left') :
                                                    (isMobile ? 
                                                        (data.textAlignmentMobile || 'center') : 
                                                        (data.textAlignment || 'left')
                                                    ),
                                                // ✅ ALIGN ITEMS: Horizontale Ausrichtung der Flex-Elemente (Mobile + Desktop Logic)
                                                alignItems: isMobile ? 'center' : 
                                                    (!isMobile && data.testimonialImageMode === true && testimonial.image ? 
                                                        (data.fullHeightImagePosition === 'right' ? 'flex-end' : 'flex-start') :
                                                        'flex-start'
                                                    )
                                            }}>
                                                {/* Testimonial Text */}
                                                <p style={{
                                                    fontSize: appendPx(data.testimonialTextSize || 18),
                                                    fontWeight: data.testimonialTextWeight || '400',
                                                    fontFamily: data.testimonialTextFont || fonts.globalSmallFont,
                                                    color: data.testimonialLayout === 'full-width-modern' ? '#ffffff' : (data.testimonialTextColor || '#333333'),
                                                    lineHeight: '1.6',
                                                    margin: '0',
                                                    fontStyle: data.testimonialItalic ? 'italic' : 'normal',
                                                    // ✅ RESPONSIVE: Text Max Width Control
                                                    maxWidth: data.testimonialTextMaxWidth ? appendPx(getResponsiveValue(data, 'testimonialTextMaxWidth', isMobile, data.testimonialTextMaxWidth)) : 'none'
                                                }}>
                                                    {data.testimonialQuotes ? `"${testimonial.text}"` : testimonial.text}
                                                </p>

                                                {/* Author Info */}
                                                <div style={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column', 
                                                    gap: '4px',
                                                    // ✅ MOBILE: Immer zentrierte Ausrichtung
                                                    // ✅ MOBILE: Zentrierte Author-Info
                                                    alignItems: isMobile ? 'center' : 
                                                        (!isMobile && data.testimonialImageMode === true && testimonial.image ? 
                                                            (data.fullHeightImagePosition === 'right' ? 'flex-end' : 'flex-start') :
                                                            'flex-start'
                                                        ),
                                                    // ✅ RESPONSIVE: Gleiche Breite wie Testimonial Text
                                                    maxWidth: data.testimonialTextMaxWidth ? appendPx(getResponsiveValue(data, 'testimonialTextMaxWidth', isMobile, data.testimonialTextMaxWidth)) : 'none',
                                                    width: '100%'
                                                }}>
                                                    {testimonial.author && (
                                                        <p style={{
                                                            fontSize: appendPx(data.testimonialAuthorSize || 16),
                                                            fontWeight: data.testimonialAuthorWeight || '600',
                                                            fontFamily: data.testimonialAuthorFont || fonts.globalBigFont,
                                                            color: data.testimonialLayout === 'full-width-modern' ? '#ffffff' : (data.testimonialAuthorColor || '#000000'),
                                                            margin: '0'
                                                        }}>
                                                            {testimonial.author}
                                                        </p>
                                                    )}
                                                    {testimonial.title && (
                                                        <p style={{
                                                            fontSize: appendPx(data.testimonialTitleSize || 14),
                                                            fontWeight: data.testimonialTitleWeight || '400',
                                                            fontFamily: data.testimonialTitleFont || fonts.globalSmallFont,
                                                            color: data.testimonialLayout === 'full-width-modern' ? 'rgba(255,255,255,0.8)' : (data.testimonialTitleColor || '#666666'),
                                                            margin: '0'
                                                        }}>
                                                            {testimonial.title}
                                                        </p>
                                                    )}
                                                    {testimonial.website && (
                                                        <a
                                                            href={testimonial.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                fontSize: appendPx(data.testimonialTitleSize || 14),
                                                                fontWeight: data.testimonialTitleWeight || '400',
                                                                fontFamily: data.testimonialTitleFont || fonts.globalSmallFont,
                                                                color: data.testimonialLayout === 'full-width-modern' ? 'rgba(255,255,255,0.9)' : '#5C80BC',
                                                                textDecoration: 'none',
                                                                margin: '0',
                                                                marginTop: '2px',
                                                                display: 'block',
                                                                transition: 'opacity 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        >
                                                            {testimonial.website.replace(/^https?:\/\//, '')}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        /* Grid Layout Structure (Original) */
                                        <>
                                            {/* Testimonial Image */}
                                            {testimonial.image && (
                                                <div style={{
                                                    width: appendPx(data.testimonialImageSize || 80),
                                                    height: appendPx(data.testimonialImageSize || 80),
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    border: data.testimonialImageBorder !== false ? 
                                                        `${data.testimonialImageBorderWidth || 2}px solid ${data.testimonialImageBorderColor || '#e0e0e0'}` : 
                                                        'none'
                                                }}>
                                                    <img
                                                        src={testimonial.image}
                                                        alt={testimonial.author}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Testimonial Text */}
                                            <p style={{
                                                fontSize: appendPx(data.testimonialTextSize || 16),
                                                fontWeight: data.testimonialTextWeight || '400',
                                                fontFamily: data.testimonialTextFont || fonts.globalSmallFont,
                                                color: data.testimonialTextColor || '#333333',
                                                lineHeight: '1.6',
                                                margin: '0',
                                                fontStyle: data.testimonialItalic ? 'italic' : 'normal',
                                                // ✅ NEW: Text Max Width Control für Grid-Layouts
                                                maxWidth: data.testimonialTextMaxWidth ? appendPx(data.testimonialTextMaxWidth) : 'none'
                                            }}>
                                                {data.testimonialQuotes ? `"${testimonial.text}"` : testimonial.text}
                                            </p>

                                            {/* Author Info */}
                                            <div style={{ 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                gap: '4px',
                                                // ✅ SAME MAX WIDTH: Gleiche Breite wie Testimonial Text
                                                maxWidth: data.testimonialTextMaxWidth ? appendPx(data.testimonialTextMaxWidth) : 'none',
                                                width: '100%'
                                            }}>
                                                {testimonial.author && (
                                                    <p style={{
                                                        fontSize: appendPx(data.testimonialAuthorSize || 16),
                                                        fontWeight: data.testimonialAuthorWeight || '600',
                                                        fontFamily: data.testimonialAuthorFont || fonts.globalBigFont,
                                                        color: data.testimonialAuthorColor || '#000000',
                                                        margin: '0'
                                                    }}>
                                                        {testimonial.author}
                                                    </p>
                                                )}
                                                {testimonial.title && (
                                                    <p style={{
                                                        fontSize: appendPx(data.testimonialTitleSize || 14),
                                                        fontWeight: data.testimonialTitleWeight || '400',
                                                        fontFamily: data.testimonialTitleFont || fonts.globalSmallFont,
                                                        color: data.testimonialTitleColor || '#666666',
                                                        margin: '0'
                                                    }}>
                                                        {testimonial.title}
                                                    </p>
                                                )}
                                                {testimonial.website && (
                                                    <a
                                                        href={testimonial.website}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: appendPx(data.testimonialTitleSize || 14),
                                                            fontWeight: data.testimonialTitleWeight || '400',
                                                            fontFamily: data.testimonialTitleFont || fonts.globalSmallFont,
                                                            color: '#5C80BC',
                                                            textDecoration: 'none',
                                                            margin: '0',
                                                            marginTop: '2px',
                                                            display: 'block',
                                                            transition: 'opacity 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                        >
                                                            {testimonial.website.replace(/^https?:\/\//, '')}
                                                        </a>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Fallback wenn keine Testimonials */}
                {testimonials.length === 0 && isEditMode && (
                    <div style={{
                        padding: '40px',
                        border: '2px dashed #dee2e6',
                        borderRadius: '12px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: 0, fontSize: '16px' }}>
                            Füge Testimonials über das Edit-Panel hinzu
                        </p>
                    </div>
                )}
            </div> {/* Content Container Ende */}
            </div> {/* Widget Container Ende */}
        </>
    );
}

export default SimplisticTestimonial;
